import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Building2, Palette, Users, BarChart3, Sparkles, ChevronDown, Send, Loader2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import ReCaptcha, { useCaptcha } from '../components/ReCaptcha';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Palette, Users, Sparkles, BarChart3, Star, Mail, Building2
};

const PublicitePage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    budget: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // CAPTCHA
  const { captchaToken, captchaKey, resetCaptcha, onVerify, onExpire, onError, isVerified, captchaConfig } = useCaptcha();
  const captchaRequired = captchaConfig?.enabled && captchaConfig?.protect_contact;

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      const res = await axios.get(`${API}/pages/publicite`);
      if (res.data) {
        setPageData(res.data);
      }
    } catch (e) {
      // Use default content if page not in CMS
      setPageData(defaultPageData);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.company || !formData.message) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs obligatoires', variant: 'destructive' });
      return;
    }
    
    if (captchaRequired && !isVerified) {
      toast({ title: 'Erreur', description: 'Veuillez valider le CAPTCHA', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        subject: `[PUBLICITÉ] Demande de ${formData.company}`,
        message: `Entreprise: ${formData.company}\nTéléphone: ${formData.phone}\nBudget: ${formData.budget}\n\nMessage:\n${formData.message}`,
        reason: 'advertising',
        captcha_token: captchaToken
      });
      toast({ title: 'Demande envoyée', description: 'Nous vous recontacterons dans les plus brefs délais' });
      setFormData({ company: '', name: '', email: '', phone: '', message: '', budget: '' });
      resetCaptcha();
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'envoyer la demande", variant: 'destructive' });
    }
    setSubmitting(false);
  };

  // Default page data (fallback)
  const defaultPageData = {
    title: "Publicité",
    sections: [
      {
        id: "hero",
        type: "hero",
        title: "Faire de la publicité sur SENDBOX",
        description: "Sur SENDBOX, nous dédions les écrans de chargement et de téléchargement de la version gratuite de notre service de transfert de fichiers aux annonceurs souhaitant utiliser notre dispositif publicitaire innovant, 100% personnalisable et immersif."
      },
      {
        id: "stats",
        type: "stats_grid",
        items: [
          { value: "2M+", label: "Utilisateurs mensuels" },
          { value: "85%", label: "Professionnels créatifs" },
          { value: "100%", label: "Écrans personnalisables" },
          { value: "15s", label: "Durée d'attention moyenne" }
        ]
      },
      {
        id: "audience",
        type: "text_section",
        title: "Notre audience",
        description: "Le cœur de notre audience est principalement constitué d'utilisateurs créatifs, urbains et curieux : professionnels de la photographie, de la vidéo, de la musique, du graphisme, etc. travaillant dans des entreprises de toutes tailles et de tous secteurs d'activité."
      },
      {
        id: "features",
        type: "features_grid",
        title: "Pourquoi choisir SENDBOX ?",
        items: [
          { icon: "Palette", title: "100% personnalisable", description: "Personnalisez entièrement l'expérience visuelle avec vos couleurs, images et vidéos" },
          { icon: "Users", title: "Audience ciblée", description: "Touchez une audience de créatifs professionnels, décideurs et passionnés" },
          { icon: "Sparkles", title: "Format immersif", description: "Captez l'attention avec un format plein écran pendant le transfert" },
          { icon: "BarChart3", title: "Mesure d'impact", description: "Suivez les performances de vos campagnes en temps réel" }
        ]
      },
      {
        id: "examples",
        type: "gallery",
        title: "Exemples de campagnes",
        subtitle: "Découvrez comment nos partenaires utilisent SENDBOX pour leurs campagnes",
        items: [
          { image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600", title: "Festival de Musique", category: "Événementiel" },
          { image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600", title: "Marque de Luxe", category: "Mode & Luxe" },
          { image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600", title: "Exposition d'Art", category: "Culture" }
        ]
      },
      {
        id: "contact",
        type: "contact_form",
        title: "Parlons-en",
        description: "Pour en savoir plus sur nos offres de partenariat publicitaire, veuillez remplir le formulaire suivant."
      }
    ]
  };

  const data = pageData || defaultPageData;

  // Helper to find section by id or type
  const getSection = (idOrType) => {
    return data.sections?.find(s => s.id === idOrType || s.type === idOrType) || null;
  };

  // Get sections by type
  const heroSection = getSection('hero');
  const statsSection = getSection('stats_grid') || getSection('stats');
  const audienceSection = getSection('text_section') || getSection('audience');
  const featuresSection = data.sections?.find(s => s.type === 'features_grid');
  const gallerySection = getSection('gallery') || getSection('examples');
  const contactSection = getSection('contact_form') || getSection('contact');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      
      <main className="pt-20">
        {/* Hero Section */}
        {heroSection && (
          <section className="relative py-20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-cyan-500/10 to-blue-600/20" />
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
            
            <div className="relative max-w-5xl mx-auto px-6 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {heroSection.title}
              </h1>
              <p className="text-lg md:text-xl text-blue-200/80 max-w-3xl mx-auto leading-relaxed">
                {heroSection.description}
              </p>
              
              <div className="mt-12 flex justify-center">
                <a href="#contact" className="group flex items-center gap-2 text-blue-300 hover:text-white transition-colors">
                  <span>Découvrir nos offres</span>
                  <ChevronDown className="w-5 h-5 animate-bounce" />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Stats Section */}
        {statsSection && statsSection.items && (
          <section className="py-16 bg-slate-900/50">
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {statsSection.items.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      {stat.value}
                    </p>
                    <p className="text-blue-200/60 mt-2">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Audience Section */}
        {audienceSection && (
          <section className="py-20">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {audienceSection.title}
                </h2>
                <p className="text-lg text-blue-200/70 max-w-3xl mx-auto">
                  {audienceSection.description}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Features Section */}
        {featuresSection && featuresSection.items && (
          <section className="py-20 bg-gradient-to-b from-slate-900/50 to-blue-950/30">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
                {featuresSection.title || "Pourquoi choisir SENDBOX ?"}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {featuresSection.items.map((feature, index) => {
                  const IconComponent = iconMap[feature.icon] || Sparkles;
                  return (
                    <div 
                      key={index}
                      className="bg-slate-800/30 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-8 hover:border-blue-500/40 transition-all group"
                    >
                      <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl w-fit mb-4 group-hover:scale-110 transition-transform">
                        <IconComponent className="w-6 h-6 text-blue-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                      <p className="text-blue-200/60">{feature.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Examples/Gallery Section */}
        {gallerySection && gallerySection.items && (
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
                {gallerySection.title || "Exemples de campagnes"}
              </h2>
              <p className="text-blue-200/60 text-center mb-12">
                {gallerySection.subtitle || "Découvrez comment nos partenaires utilisent SENDBOX pour leurs campagnes"}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {gallerySection.items.map((example, index) => (
                  <div 
                    key={index}
                    className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer"
                  >
                    <img 
                      src={example.image} 
                      alt={example.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-xs text-blue-300 bg-blue-500/20 px-2 py-1 rounded-full">
                        {example.category}
                      </span>
                      <h3 className="text-xl font-semibold text-white mt-2">{example.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact Form Section */}
        <section id="contact" className="py-20 bg-gradient-to-b from-blue-950/30 to-slate-900">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {contactSection?.title || "Parlons-en"}
              </h2>
              <p className="text-blue-200/70">
                {contactSection?.description || "Pour en savoir plus sur nos offres de partenariat publicitaire, veuillez remplir le formulaire suivant."}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="bg-slate-800/30 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-8 space-y-6" data-testid="publicite-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Entreprise *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
                    placeholder="Nom de votre entreprise"
                    required
                    data-testid="publicite-company"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Nom complet</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
                    placeholder="Votre nom"
                    data-testid="publicite-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
                    placeholder="votre@email.com"
                    required
                    data-testid="publicite-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
                    placeholder="+33 6 00 00 00 00"
                    data-testid="publicite-phone"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-blue-300 mb-2">Budget estimé</label>
                <select
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-400"
                  data-testid="publicite-budget"
                >
                  <option value="">Sélectionnez un budget</option>
                  <option value="< 5 000 €">Moins de 5 000 €</option>
                  <option value="5 000 € - 15 000 €">5 000 € - 15 000 €</option>
                  <option value="15 000 € - 50 000 €">15 000 € - 50 000 €</option>
                  <option value="> 50 000 €">Plus de 50 000 €</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-blue-300 mb-2">Votre projet *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={5}
                  className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 resize-none"
                  placeholder="Décrivez votre projet publicitaire, vos objectifs et vos attentes..."
                  required
                  data-testid="publicite-message"
                />
              </div>
              
              {/* CAPTCHA */}
              {captchaRequired && (
                <div className="flex justify-center">
                  <ReCaptcha 
                    key={captchaKey}
                    onVerify={onVerify} 
                    onExpire={onExpire}
                    onError={onError}
                    siteKey={captchaConfig?.site_key}
                  />
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={submitting || (captchaRequired && !isVerified)}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-6 text-lg disabled:opacity-50"
                data-testid="publicite-submit"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                Envoyer ma demande
              </Button>
            </form>
          </div>
        </section>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default PublicitePage;
